import { DeleteOutlined, HolderOutlined, PlusCircleFilled } from '@ant-design/icons'
import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { CONSTANT } from '@salescore/client-base'
import {
  CASE_CONSIDERABLE_FILTER_TYPES,
  hasCaseInsensitiveFilterWarning,
  hasWildcardSearchFilterWarning,
  includeFilterTypes,
  startsWithFilterTypes,
  viewConfigPropertyFilterSchema,
} from '@salescore/core'
import { Alert, Button, Checkbox, Flex, Form, Select, Space } from 'antd'
import { isNotNullish } from 'effect/Predicate'
import { t } from 'i18next'
import { type CSSProperties, type ReactElement, type ReactNode, useRef } from 'react'

import { useGetModelProperty } from '../../../../../recoil/view/selectors/connectionsSelector'
import { useModelSearcher } from '../../../../../recoil/view/selectors/modelSearcherSelector'
import type { NodeWithModel } from '../common/nodeWithModel'
import { type FilterFormItemState, useGetFilterTypeLabel, type ViewConfigFilterNodeForForm } from './common'
import {
  filterChildrenEndIdAttribute,
  filterGroupFrameIdAttribute,
  filterGroupIdAttribute,
  filterLeafIdAttribute,
} from './filterDnd'
import {
  createEmptyGroup,
  type FilterFormItemStateWithId,
  type FilterTreeNodeId,
  newFilterTreeNodeId,
  type ViewConfigFilterNodeForFormWithId,
} from './filterTreeNode'
import { ViewFilterFormItems } from './ViewFilterFormItems'

export const initialState: ViewConfigFilterNodeForForm = {
  logicalOperator: 'and',
  leafs: [],
  children: [],
}

export type FilterFormNodeState = ViewConfigFilterNodeForFormWithId

interface UpdateGroupParameters {
  groupId: FilterTreeNodeId
  transform: (node: ViewConfigFilterNodeForFormWithId) => ViewConfigFilterNodeForFormWithId
  option?: { asInitialization?: boolean }
}

export const ViewFilterNodeFormItem = ({
  filterNodeState,
  propertiesWithNode,
  isAdvancedMode,
  inNestedForm,
  isRoot,
  updateGroup,
  onDestroy,
  showWarning,
}: {
  filterNodeState: FilterFormNodeState
  propertiesWithNode: NodeWithModel[]
  isAdvancedMode: boolean
  inNestedForm?: boolean
  isRoot: boolean
  updateGroup: (parameters: UpdateGroupParameters) => void
  onDestroy?: () => void
  showWarning?: boolean
}): ReactElement => {
  const divReference = useRef<HTMLDivElement>(null)

  // SortableContext は ViewFilterTreeForm のルートに1つだけ置く方針なので、
  // ここでは per-parent SortableContext を入れずに直接描画する。
  const content = (
    <FilterNodeContent
      filterNodeState={filterNodeState}
      propertiesWithNode={propertiesWithNode}
      isAdvancedMode={isAdvancedMode}
      inNestedForm={inNestedForm}
      updateGroup={updateGroup}
      onDestroy={onDestroy}
      showWarning={showWarning}
      divReference={divReference}
    />
  )

  // ルートは drag handle を出さない（外側から動かす意味がないため）
  if (isRoot || !isAdvancedMode) {
    return (
      <div
        ref={divReference}
        tabIndex={-1}
        {...filterGroupIdAttribute(filterNodeState._id)}
        {...filterGroupFrameIdAttribute(filterNodeState._id)}
        className="mb-4 rounded-lg border border-solid border-gray-300 bg-gray-100 p-6"
      >
        {content}
      </div>
    )
  }
  return (
    <DraggableGroupContainer groupId={filterNodeState._id} divReference={divReference}>
      {content}
    </DraggableGroupContainer>
  )
}

/** グループ1ノードの中身（リーフ行・子グループ・追加ボタン）。外殻（枠 / DnD ラッパー）は ViewFilterNodeFormItem 側が持つ。 */
const FilterNodeContent = ({
  filterNodeState,
  propertiesWithNode,
  isAdvancedMode,
  inNestedForm,
  updateGroup,
  onDestroy,
  showWarning,
  divReference,
}: {
  filterNodeState: FilterFormNodeState
  propertiesWithNode: NodeWithModel[]
  isAdvancedMode: boolean
  inNestedForm?: boolean
  updateGroup: (parameters: UpdateGroupParameters) => void
  onDestroy?: () => void
  showWarning?: boolean
  divReference: React.RefObject<HTMLDivElement>
}): ReactElement => {
  const getModelProperty = useGetModelProperty()
  const modelSearcher = useModelSearcher()
  const getFilterTypeLabel = useGetFilterTypeLabel()

  const getCaseInsensitiveFilterWarningMessage = (filterState: FilterFormItemState): string | undefined => {
    const parsedFilter = viewConfigPropertyFilterSchema.safeParse({
      type: 'property', // フォームで作成されるフィルタは全てpropertyフィルタ
      ...filterState,
    }).data

    if (parsedFilter === undefined || !hasCaseInsensitiveFilterWarning(parsedFilter, modelSearcher)) {
      return
    }
    return t('大文字と小文字を区別しないと表示に時間がかかるため、必要がない場合は区別することを推奨します。')
  }

  const getWildcardSearchFilterWarningMessage = (filterState: FilterFormItemState): string | undefined => {
    const parsedFilter = viewConfigPropertyFilterSchema.safeParse({
      type: 'property', // フォームで作成されるフィルタは全てpropertyフィルタ
      ...filterState,
    }).data

    if (parsedFilter === undefined || !hasWildcardSearchFilterWarning(parsedFilter, modelSearcher)) {
      return
    }

    const property = getModelProperty(parsedFilter.property.modelName, parsedFilter.property.propertyName)
    const filterTypeLabel = getFilterTypeLabel(property?.type, parsedFilter.filterType)

    return t(
      '「{{filterTypeLabel}}」の条件を用いると表示に時間がかかるため、必要がない場合は「等しい」「異なる」「次の文字列から始まる」「次の文字列から始まらない」などの設定に変更することを推奨します。',
      { filterTypeLabel },
    )
  }

  const getFilterWarningMessages = (filterState: FilterFormItemState): string[] =>
    [getCaseInsensitiveFilterWarningMessage(filterState), getWildcardSearchFilterWarningMessage(filterState)].filter(
      isNotNullish,
    )

  const shouldDisplayCaseSensitiveForm = (filterState: FilterFormItemState): boolean => {
    if (filterState.filterType === undefined || filterState.property === undefined) {
      return false
    }
    const {
      filterType,
      property: { modelName, propertyName },
    } = filterState
    // XXX: id項目はgetModelPropertyで検索できないが、文字列型なので判定対象とする
    if (propertyName === 'id') return CASE_CONSIDERABLE_FILTER_TYPES.has(filterType)

    const property = getModelProperty(modelName, propertyName)
    if (property?.type !== 'string') return false
    return isNotNullish(property.selectOptions) && property.selectOptions.length > 0
      ? ([...includeFilterTypes, ...startsWithFilterTypes] as string[]).includes(filterType)
      : CASE_CONSIDERABLE_FILTER_TYPES.has(filterType)
  }

  const setLeafAt = (
    index: number,
    newFilter: FilterFormItemState | undefined,
    option?: { asInitialization?: boolean },
  ): void => {
    updateGroup({
      groupId: filterNodeState._id,
      transform: (node) => {
        if (newFilter === undefined) {
          return { ...node, leafs: node.leafs.filter((_, currentIndex) => currentIndex !== index) }
        }
        const existing = node.leafs.at(index)
        const id = existing?._id ?? newFilterTreeNodeId()
        const withId: FilterFormItemStateWithId = { ...newFilter, _id: id }
        if (index >= node.leafs.length) {
          return { ...node, leafs: [...node.leafs, withId] }
        }
        return { ...node, leafs: node.leafs.map((leaf, currentIndex) => (currentIndex === index ? withId : leaf)) }
      },
      option,
    })
  }

  const removeChildGroupAt = (index: number): void => {
    updateGroup({
      groupId: filterNodeState._id,
      transform: (node) => ({ ...node, children: node.children.filter((_, currentIndex) => currentIndex !== index) }),
    })
  }
  return (
    <>
      {filterNodeState.leafs.map((leafState, index) => (
        // divider は SortableLeafRow の外に出す。
        // 中に入れるとドラッグ transform で AND/OR セレクタが一緒に動いてしまうため。
        <div key={leafState._id}>
          <SortableLeafRow leafId={leafState._id} showHandle={isAdvancedMode}>
            <ViewFilterFormItems
              filter={leafState}
              onChange={(newFilterState: FilterFormItemState, option?: { asInitialization?: boolean }) => {
                setLeafAt(index, newFilterState, option)
              }}
              propertiesWithNode={propertiesWithNode}
            >
              <Button
                icon={<DeleteOutlined />}
                type="text"
                style={{ marginTop: '6px' }}
                onClick={() => {
                  // 削除でフォーカスが外れて保存ショートカットが効かなくなるため、削除後に div にフォーカスを戻す
                  divReference.current?.focus()
                  setLeafAt(index, undefined)
                }}
              />
            </ViewFilterFormItems>
            {shouldDisplayCaseSensitiveForm(leafState) && (
              <Form.Item className={inNestedForm === true ? 'mt-6' : ''} style={{ minHeight: 32 }}>
                <Checkbox
                  checked={leafState.option?.shouldConsiderCase === true}
                  onChange={(value) => {
                    setLeafAt(index, {
                      ...leafState,
                      option: {
                        ...leafState.option,
                        shouldConsiderCase: value.target.checked,
                      },
                    })
                  }}
                >
                  {t('大文字と小文字を区別する')}
                </Checkbox>
              </Form.Item>
            )}
            {showWarning === true && (
              <Flex vertical gap={8}>
                {getFilterWarningMessages(leafState).map((message) => (
                  <Alert key={message} message={message} type="warning" showIcon closable />
                ))}
              </Flex>
            )}
          </SortableLeafRow>
          <DividerWithLogicalOperator
            hasTopSpacing={inNestedForm === true}
            logicalOperator={filterNodeState.logicalOperator}
            onLogicalOperatorChange={(value) => {
              updateGroup({ groupId: filterNodeState._id, transform: (node) => ({ ...node, logicalOperator: value }) })
            }}
            // 最後のリーフの直後に区切りを出すと、後続の子グループとの間に二重に区切りが入るのを避ける
            withLogicalOperator={index !== filterNodeState.leafs.length - 1 && isAdvancedMode}
          />
        </div>
      ))}
      {isAdvancedMode &&
        filterNodeState.children.map((childFilterNodeState, childFilterNodeStateIndex) => (
          <div key={childFilterNodeState._id}>
            <DividerWithLogicalOperator
              hasTopSpacing={childFilterNodeStateIndex > 0}
              logicalOperator={filterNodeState.logicalOperator}
              onLogicalOperatorChange={(value) => {
                updateGroup({
                  groupId: filterNodeState._id,
                  transform: (node) => ({ ...node, logicalOperator: value }),
                })
              }}
              withLogicalOperator
            />
            <ViewFilterNodeFormItem
              inNestedForm={inNestedForm}
              isAdvancedMode
              isRoot={false}
              filterNodeState={childFilterNodeState}
              propertiesWithNode={propertiesWithNode}
              updateGroup={updateGroup}
              onDestroy={() => {
                removeChildGroupAt(childFilterNodeStateIndex)
              }}
              showWarning={showWarning}
            />
          </div>
        ))}

      {/* 子要素の末尾位置（＝「条件を追加」ボタンの直上）。DnD のドロップ位置インジケータが
          「このグループの中の末尾」を枠内の下部に表示するための目印。 */}
      <div {...filterChildrenEndIdAttribute(filterNodeState._id)} />

      <Space>
        <Button
          onClick={() => {
            const newLeaf: FilterFormItemStateWithId = { _id: newFilterTreeNodeId() }
            updateGroup({
              groupId: filterNodeState._id,
              transform: (node) => ({ ...node, leafs: [...node.leafs, newLeaf] }),
            })
          }}
          block
          icon={<PlusCircleFilled />}
          type="text"
          style={{
            color: CONSTANT.colors.primaryColor,
          }}
        >
          {t(`条件を追加`)}
        </Button>
        {isAdvancedMode && (
          <>
            <Button
              onClick={() => {
                const newGroup = createEmptyGroup()
                updateGroup({
                  groupId: filterNodeState._id,
                  transform: (node) => ({ ...node, children: [...node.children, newGroup] }),
                })
              }}
              block
              icon={<PlusCircleFilled />}
              type="text"
              style={{
                color: CONSTANT.colors.primaryColor,
              }}
            >
              {t(`グループを追加`)}
            </Button>

            {onDestroy !== undefined && (
              <Button
                icon={<DeleteOutlined />}
                type="text"
                onClick={() => {
                  onDestroy()
                }}
                style={{
                  color: CONSTANT.colors.primaryColor,
                }}
              >
                {t(`削除`)}
              </Button>
            )}
          </>
        )}
      </Space>
    </>
  )
}

/**
 * 非ルートのグループ本体。SortableContext 内で利用する前提で useSortable を呼ぶ。
 * drag handle はグループ枠の左外側に置き、枠の高さ方向の中央に揃える。
 */
const DraggableGroupContainer = ({
  groupId,
  divReference,
  children,
}: {
  groupId: FilterTreeNodeId
  divReference: React.RefObject<HTMLDivElement>
  children: ReactNode
}): ReactElement => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: groupId })
  const style: CSSProperties = { transform: CSS.Translate.toString(transform), transition }
  // ドラッグ中は実体がポインタに追従する。掴んでいることが分かる程度に軽く半透明＋前面化。
  const draggingClassName = isDragging ? 'relative z-50 opacity-85' : ''
  return (
    <div
      ref={setNodeRef}
      style={style}
      {...filterGroupIdAttribute(groupId)}
      className={`mb-4 flex items-center gap-2 ${draggingClassName}`}
    >
      <HolderOutlined
        data-testid={`filter-group-drag-handle-${groupId}`}
        aria-label={t(`ドラッグして並び替え`)}
        className="cursor-grab text-gray-400 hover:text-gray-600"
        {...attributes}
        {...listeners}
      />
      <div
        ref={divReference}
        tabIndex={-1}
        {...filterGroupFrameIdAttribute(groupId)}
        className="flex-1 rounded-lg border border-solid border-gray-300 bg-gray-100 p-6"
      >
        {children}
      </div>
    </div>
  )
}

/**
 * リーフ行の SortableItem ラッパー。
 * drag handle は「絞り込み項目」ラベルではなく、その下の入力欄の高さに揃える。
 * antd の vertical Form.Item ラベル分（label 行の高さ）だけ下にオフセットする。
 */
const SortableLeafRow = ({
  leafId,
  showHandle,
  children,
}: {
  leafId: FilterTreeNodeId
  showHandle: boolean
  children: ReactNode
}): ReactElement => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: leafId })
  const style: CSSProperties = { transform: CSS.Translate.toString(transform), transition }
  // ドラッグ中は実体がポインタに追従する。掴んでいることが分かる程度に軽く半透明＋前面化。
  const draggingClassName = isDragging ? 'relative z-50 opacity-85' : ''
  return (
    <div
      ref={setNodeRef}
      style={style}
      {...filterLeafIdAttribute(leafId)}
      className={`flex items-start gap-2 ${draggingClassName}`}
    >
      {showHandle && (
        // Form.Item の vertical ラベル(高さ ≒ 30px) を超えて入力欄の中央に来るようオフセット。
        // 入力欄(高さ 32px)の中央 ≒ 30 + 16 = 46px。アイコン自体の高さ分を引いて 38px に設定。
        <HolderOutlined
          data-testid={`filter-leaf-drag-handle-${leafId}`}
          aria-label={t(`ドラッグして並び替え`)}
          className="mt-[38px] cursor-grab text-gray-400 hover:text-gray-600"
          {...attributes}
          {...listeners}
        />
      )}
      <div className="flex-1">{children}</div>
    </div>
  )
}

const DividerWithLogicalOperator = ({
  withLogicalOperator,
  logicalOperator,
  hasTopSpacing,
  onLogicalOperatorChange,
}: {
  withLogicalOperator: boolean
  logicalOperator: 'and' | 'or'
  /** 条件の select の上に mergin を追加する */
  hasTopSpacing: boolean
  onLogicalOperatorChange: (op: 'and' | 'or') => void
}): ReactElement => (
  <div className={`relative mb-8 ${hasTopSpacing ? 'mt-8' : ''}`}>
    <hr className="opacity-30" />
    {withLogicalOperator && (
      <Select
        style={{
          position: 'absolute',
          top: -16,
        }}
        value={logicalOperator}
        onChange={(value) => {
          onLogicalOperatorChange(value)
        }}
        options={[
          {
            label: t(`AND条件`),
            value: 'and',
          },
          {
            label: t(`OR条件`),
            value: 'or',
          },
        ]}
      />
    )}
  </div>
)
